import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { X, Loader2, Bell, CheckCircle, Monitor, PenTool } from "lucide-react";
import { API } from "./constants";

const productInfo = {
  phoenix_it_hub: {
    name: "Phoenix IT Hub",
    tagline: "Complete IT management suite",
    icon: Monitor,
    color: "amber",
    description: "Be the first to know when we launch our AI-powered IT management solution. Get backup & recovery, network analysis, remote access security, and system maintenance all in one place."
  },
  scribe_flow_studio: {
    name: "Scribe Flow Studio",
    tagline: "AI writing ecosystem",
    icon: PenTool,
    color: "purple",
    description: "Join the waitlist for our AI-powered writing platform. Document generation, content optimization, and multi-format export capabilities coming soon."
  }
};

const WaitlistModal = ({ isOpen, onClose, product }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const info = productInfo[product];
  const Icon = info?.icon || Bell;
  const colorClass = info?.color === "amber" ? "amber" : "purple";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        product: product,
        name: name.trim()
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join waitlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setName("");
    setSuccess(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <Card className={`relative w-full max-w-md border-${colorClass}-500/50 bg-background`}>
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>

        <CardHeader className="text-center pb-2">
          <div className={`w-16 h-16 rounded-full bg-${colorClass}-500/10 flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 text-${colorClass}-400`} />
          </div>
          <CardTitle className="text-xl font-heading">{info?.name}</CardTitle>
          <CardDescription>{info?.tagline}</CardDescription>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">You're on the list!</h3>
                <p className="text-sm text-muted">
                  We'll notify you at <span className="text-foreground">{email}</span> when {info?.name} launches.
                </p>
              </div>
              <Button onClick={handleClose} variant="outline" className="mt-4">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted mb-4">
                {info?.description}
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="waitlist-email">Email Address *</Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="waitlist-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="waitlist-name">Name (Optional)</Label>
                <Input
                  id="waitlist-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="waitlist-name-input"
                />
              </div>

              <Button 
                type="submit" 
                className={`w-full bg-${colorClass}-500 hover:bg-${colorClass}-600`}
                disabled={loading}
                data-testid="waitlist-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining...</>
                ) : (
                  <><Bell className="w-4 h-4 mr-2" /> Notify Me When It Launches</>
                )}
              </Button>

              <p className="text-xs text-center text-muted">
                No spam, ever. We'll only email you when we launch.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitlistModal;
